import { useEffect } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'

import Icon from '../Icon'
import FilterField from './FilterField'

import styles from './style.module.scss'

/*
 * Die Filterleiste — eine fuer alle Listen.
 *
 * Faehrt von rechts herein und nimmt der Tabelle keinen senkrechten Platz weg.
 * Welche Felder sie zeigt, sagt die Ansicht ueber `fields` (siehe fields.js);
 * die Leiste selbst kennt keine einzige Ansicht. Eine neue Liste braucht damit
 * nur ihre Feldliste und keinen Eingriff hier.
 *
 * Gefiltert wird sofort beim Auswaehlen — es gibt keinen Anwenden-Knopf mehr.
 * Deshalb steht in der Fusszeile nur Zuruecksetzen und Schliessen.
 */

export default function FilterPanel({ open, onClose, fields, values, count, onChange, onResetAll }) {
	const { t } = useTranslation()

	useEffect(() => {
		if (!open) return undefined

		const onKeyDown = event => {
			if (event.key === 'Escape') onClose()
		}
		document.addEventListener('keydown', onKeyDown)
		return () => document.removeEventListener('keydown', onKeyDown)
	}, [open, onClose])

	return (
		<>
			<div className={clsx(styles.overlay, open && styles.overlayOpen)} onClick={onClose} />

			<aside
				className={clsx(styles.root, open && styles.rootOpen)}
				role="dialog"
				aria-label={t('Filter')}
				aria-hidden={!open}
			>
				<header className={styles.header}>
					<h2 className={styles.title}>
						{t('Filter')}
						{count > 0 && <span className={styles.badge}>{count}</span>}
					</h2>
					<button type="button" className={styles.close} onClick={onClose} title={t('Close')}>
						<Icon name="close" size={{ w: 12, h: 12 }} noFill={true} />
					</button>
				</header>

				<div className={styles.body}>
					{fields.map(field => (
						<FilterField
							key={field.key}
							field={field}
							values={values[field.key] || []}
							onChange={selected => onChange(field.key, selected)}
						/>
					))}
				</div>

				<footer className={styles.footer}>
					<button type="button" className={styles.reset} onClick={onResetAll} disabled={!count}>
						{t('Reset filters')}
					</button>
					<button type="button" className={styles.done} onClick={onClose}>
						{t('Close')}
					</button>
				</footer>
			</aside>
		</>
	)
}

FilterPanel.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	fields: PropTypes.array.isRequired,
	values: PropTypes.object.isRequired,
	count: PropTypes.number.isRequired,
	onChange: PropTypes.func.isRequired,
	onResetAll: PropTypes.func.isRequired
}
